/**
 * ========================
 * MEETINGS DOMAIN MENU PROVIDER
 * ========================
 *
 * Additive, permission-gated menu for the Meetings domain. The three audiences
 * are NOT mutually exclusive — one user can be all of them at once, so each
 * section is shown independently based on what the user can do:
 *
 *  1. Overview        — Dashboard                                   (any meeting user)
 *  2. My Meetings     — My Meetings, Invitations (RSVP), Calendar,  (attendee + organizer)
 *                       My Action Items
 *  3. Organize        — New Meeting, Book a Venue                   (creators: Meeting.Write)
 *  4. Administration  — All Meetings, Venue Bookings, Venues &      (admins: Meeting.Admin)
 *                       Setup, Reports, Lookups, Settings
 *
 * A user with Meeting.Read sees 1+2; add Meeting.Write to also get 3; add
 * Meeting.Admin (or the Admin/MeetingAdmin role) to also get 4.
 */

import {
  IconLayoutDashboard,
  IconCalendarEvent,
  IconMailOpened,
  IconCalendar,
  IconChecklist,
  IconPlus,
  IconBuildingCommunity,
  IconCalendarStats,
  IconTicket,
  IconReportAnalytics,
  IconTableOptions,
  IconSettings,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck, anyVisibilityCheck } from './BaseMenuProvider';
import { MEETINGS_ROUTES } from '@/config/routes';

const MEETING_ADMIN_ROLES = ['MeetingAdmin', 'Admin'];

class MeetingsMenuProvider implements DomainMenuProvider {
  domainId = 'meetings';

  getMenuConfig(): DomainMenuConfig {
    // Attendee surfaces — any authenticated staff member can be invited to a meeting.
    const allUsers = createVisibilityCheck();
    // Organizers — can create meetings / book venues.
    const creators = anyVisibilityCheck({ roles: MEETING_ADMIN_ROLES }, { permissions: ['Meeting.Write'] });
    // Administrators — full control over the domain.
    const admins = anyVisibilityCheck({ roles: MEETING_ADMIN_ROLES }, { permissions: ['Meeting.Admin'] });

    const sections: MenuSection[] = [
      // 1. Overview
      {
        id: 'meetings-overview',
        label: 'Overview',
        visible: allUsers,
        items: [
          {
            id: 'meetings-dashboard',
            label: 'Dashboard',
            path: MEETINGS_ROUTES.DASHBOARD,
            icon: <IconLayoutDashboard size={16} />,
            visible: allUsers,
          },
        ],
      },

      // 2. My Meetings (attendee + organizer)
      {
        id: 'meetings-my',
        label: 'My Meetings',
        visible: allUsers,
        items: [
          {
            id: 'meetings-my-meetings',
            label: 'My Meetings',
            path: MEETINGS_ROUTES.MY_MEETINGS,
            icon: <IconCalendarEvent size={16} />,
            visible: allUsers,
          },
          {
            id: 'meetings-invitations',
            label: 'Invitations',
            path: MEETINGS_ROUTES.INVITATIONS,
            icon: <IconMailOpened size={16} />,
            visible: allUsers,
          },
          {
            id: 'meetings-calendar',
            label: 'Calendar',
            path: MEETINGS_ROUTES.CALENDAR,
            icon: <IconCalendar size={16} />,
            visible: allUsers,
          },
          {
            id: 'meetings-my-action-items',
            label: 'My Action Items',
            path: MEETINGS_ROUTES.MY_ACTION_ITEMS,
            icon: <IconChecklist size={16} />,
            visible: allUsers,
          },
        ],
      },

      // 3. Organize (creators)
      {
        id: 'meetings-organize',
        label: 'Organize',
        visible: creators,
        items: [
          {
            id: 'meetings-new',
            label: 'New Meeting',
            path: MEETINGS_ROUTES.CREATE,
            icon: <IconPlus size={16} />,
            visible: creators,
          },
          {
            id: 'meetings-book-venue',
            label: 'Book a Venue',
            path: MEETINGS_ROUTES.BOOK_VENUE,
            icon: <IconBuildingCommunity size={16} />,
            visible: creators,
          },
        ],
      },

      // 4. Administration (admins)
      {
        id: 'meetings-admin',
        label: 'Administration',
        visible: admins,
        items: [
          {
            id: 'meetings-admin-all',
            label: 'All Meetings',
            path: MEETINGS_ROUTES.ADMIN_ALL_MEETINGS,
            icon: <IconCalendarStats size={16} />,
            visible: admins,
          },
          {
            id: 'meetings-admin-bookings',
            label: 'Venue Bookings',
            path: MEETINGS_ROUTES.ADMIN_BOOKINGS,
            icon: <IconTicket size={16} />,
            visible: admins,
          },
          {
            id: 'meetings-admin-venues',
            label: 'Venues & Setup',
            path: MEETINGS_ROUTES.ADMIN_VENUES,
            icon: <IconBuildingCommunity size={16} />,
            visible: admins,
          },
          {
            id: 'meetings-admin-reports',
            label: 'Reports',
            path: MEETINGS_ROUTES.ADMIN_REPORTS,
            icon: <IconReportAnalytics size={16} />,
            visible: admins,
          },
          {
            id: 'meetings-admin-lookups',
            label: 'Lookups',
            path: MEETINGS_ROUTES.ADMIN_LOOKUPS,
            icon: <IconTableOptions size={16} />,
            visible: admins,
          },
          {
            id: 'meetings-admin-settings',
            label: 'Settings',
            path: MEETINGS_ROUTES.ADMIN_SETTINGS,
            icon: <IconSettings size={16} />,
            visible: admins,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '3.0.0',
        lastUpdated: Date.now(),
        description: 'Meeting Management — additive attendee / organizer / admin menu',
      },
    };
  }
}

export const meetingsMenuProvider = new MeetingsMenuProvider();
